import React from "react";
import useReportFormat from "../useReportFormat";

const cardBase = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
  border: "1px solid #f0f4ff",
};

const labelStyle = {
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "#64748b",
  marginBottom: 0,
};

const amountStyle = {
  fontSize: "1.75rem",
  fontWeight: 800,
  color: "#002045",
  letterSpacing: "-0.03em",
  margin: "8px 0",
};

const sectionLabel = {
  fontSize: "0.6rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "#94a3b8",
  marginBottom: 12,
  marginTop: 0,
  paddingBottom: 8,
  borderBottom: "1px solid #f1f5f9",
};

const CostKpiCards = ({
  totalBudget = 0,
  totalSpent = 0,
  remaining = 0,
  unexpectedBudget = 0,
  unexpectedSpent = 0,
  unexpectedRemaining = 0,
  unexpectedAbsorbed = 0,
}) => {
  const formatCurrency = useReportFormat();
  const spentPct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
  const unexpectedUsedPct = unexpectedBudget > 0
    ? Math.round(((unexpectedSpent + unexpectedAbsorbed) / unexpectedBudget) * 100)
    : 0;

  return (
    <div style={{ marginBottom: 32 }}>
      <p style={sectionLabel}>Costs</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 24,
        }}
      >
        {/* Total Budget */}
        <div style={cardBase}>
          <p style={labelStyle}>Total Cost Budget</p>
          <div style={amountStyle}>{formatCurrency(totalBudget)}</div>
        </div>

        {/* Total Spent */}
        <div style={{ ...cardBase, borderLeft: "4px solid #002045" }}>
          <p style={labelStyle}>Total Spent</p>
          <div style={amountStyle}>{formatCurrency(totalSpent)}</div>
          <div style={{ height: 4, background: "#e2e8f0", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min(spentPct, 100)}%`,
                background: spentPct > 100 ? "#ef4444" : spentPct > 80 ? "#f59e0b" : "#002045",
                borderRadius: 99,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <p style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, marginTop: 6, marginBottom: 0 }}>
            {spentPct}% of budget used
          </p>
        </div>

        {/* Remaining Budget */}
        <div style={cardBase}>
          <p style={labelStyle}>Remaining Budget</p>
          <div style={{ ...amountStyle, color: remaining < 0 ? "#ef4444" : "#002045" }}>
            {formatCurrency(Math.abs(remaining))}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: remaining < 0 ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${remaining < 0 ? "#fecaca" : "#bbf7d0"}`,
              borderRadius: 99,
              padding: "3px 10px",
              marginTop: 4,
            }}
          >
            <i
              className={remaining < 0 ? "ri-alert-fill" : "ri-checkbox-circle-fill"}
              style={{ color: remaining < 0 ? "#ef4444" : "#059669", fontSize: "0.85rem" }}
            />
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: remaining < 0 ? "#ef4444" : "#059669" }}>
              {remaining < 0 ? "Over Budget" : "Within Budget"}
            </span>
          </div>
        </div>

        {/* Unexpected / Contingency Pool */}
        <div style={{ ...cardBase, borderLeft: `4px solid ${unexpectedRemaining < 0 ? "#ef4444" : "#f59e0b"}` }}>
          <p style={labelStyle}>Contingency Budget</p>
          <div style={{ ...amountStyle, color: unexpectedRemaining < 0 ? "#ef4444" : "#b45309" }}>
            {formatCurrency(unexpectedBudget)}
          </div>
          <div style={{ height: 4, background: "#fef3c7", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min(unexpectedUsedPct, 100)}%`,
                background: unexpectedRemaining < 0 ? "#ef4444" : "#f59e0b",
                borderRadius: 99,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <p style={{ fontSize: "0.72rem", color: "#92400e", fontWeight: 600, margin: 0 }}>
              {unexpectedUsedPct}% used
            </p>
            <p style={{ fontSize: "0.72rem", fontWeight: 700, margin: 0, color: unexpectedRemaining < 0 ? "#ef4444" : "#059669" }}>
              {unexpectedRemaining < 0 ? "−" : "+"}{formatCurrency(Math.abs(unexpectedRemaining))} left
            </p>
          </div>
          {unexpectedAbsorbed > 0 && (
            <p style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 500, marginTop: 4, marginBottom: 0 }}>
              {formatCurrency(unexpectedAbsorbed)} absorbed from overruns
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostKpiCards;
